import { useState, type FormEvent } from "react";
import type { TierUpsert } from "../../../hooks/useAdminTiers";
import type { MutationResult, Tier } from "../../../types/billing";
import styles from "./TierFormModal.module.css";

type Props = {
  readonly mode: "create" | "edit";
  readonly initial?: Tier;
  readonly onSubmit: (body: TierUpsert) => Promise<MutationResult>;
  readonly onClose: () => void;
};

// Create / edit a tier_definition. tier_code is immutable, so it's only an input
// in create mode (shown read-only when editing).
export const TierFormModal = ({ mode, initial, onSubmit, onClose }: Props): JSX.Element => {
  const [tierCode, setTierCode] = useState(initial?.tierCode ?? "");
  const [displayName, setDisplayName] = useState(initial?.displayName ?? "");
  const [rank, setRank] = useState(String(initial?.rank ?? ""));
  const [slots, setSlots] = useState(String(initial?.includedSlots ?? 0));
  const [coaching, setCoaching] = useState(String(initial?.includedCoachingSessions ?? 0));
  const [trialDays, setTrialDays] = useState(String(initial?.trialDays ?? 0));
  const [active, setActive] = useState(initial?.active ?? true);
  const [planName, setPlanName] = useState(initial?.planItem?.displayName ?? "");
  const [planDescription, setPlanDescription] = useState(initial?.planItem?.description ?? "");
  const [setupFeeName, setSetupFeeName] = useState(initial?.setupFeeItem?.displayName ?? "");
  const [setupFeeDescription, setSetupFeeDescription] = useState(initial?.setupFeeItem?.description ?? "");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);

    const displayNameTrimmed = displayName.trim();
    if (displayNameTrimmed === "") return setError("Display name is required");

    // In edit mode, only send checkout copy when the loaded tier carried it --
    // a stale pre-copy cache would otherwise clear server-side descriptions
    // with the unprefilled (empty) fields.
    const copyKnown = mode === "create" || initial?.planItem !== undefined;
    const common = {
      displayName: displayNameTrimmed,
      rank: Number(rank) || 0,
      includedSlots: Number(slots) || 0,
      includedCoachingSessions: Number(coaching) || 0,
      trialDays: Number(trialDays) || 0,
      active,
      ...(copyKnown && {
        planName: planName.trim(),
        planDescription: planDescription.trim(),
        setupFeeName: setupFeeName.trim(),
        setupFeeDescription: setupFeeDescription.trim(),
      }),
    };
    let body: TierUpsert;
    if (mode === "create") {
      const code = tierCode.trim();
      if (code === "") return setError("Tier code is required");
      body = { ...common, tierCode: code };
    } else {
      body = common;
    }

    setSaving(true);
    const result = await onSubmit(body);
    setSaving(false);
    if (result.ok) onClose();
    else setError(result.message);
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-label={mode === "create" ? "New tier" : "Edit tier"}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className={styles.title}>{mode === "create" ? "New tier" : `Edit ${initial?.displayName ?? ""}`}</h3>

        <form className={styles.form} onSubmit={submit}>
          {mode === "create" ? (
            <label className={styles.field}>
              <span>Tier code</span>
              <input
                value={tierCode}
                onChange={(e) => setTierCode(e.target.value)}
                placeholder="e.g. scale"
                autoFocus
              />
            </label>
          ) : (
            <div className={styles.field}>
              <span>Tier code</span>
              <code className={styles.codeReadonly}>{initial?.tierCode}</code>
            </div>
          )}

          <label className={styles.field}>
            <span>Display name</span>
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </label>

          <div className={styles.row}>
            <label className={styles.field}>
              <span>Rank</span>
              <input type="number" value={rank} onChange={(e) => setRank(e.target.value)} />
            </label>
            <label className={styles.field}>
              <span>Included slots</span>
              <input type="number" min={0} value={slots} onChange={(e) => setSlots(e.target.value)} />
            </label>
            <label className={styles.field}>
              <span>Included coaching</span>
              <input type="number" min={0} value={coaching} onChange={(e) => setCoaching(e.target.value)} />
            </label>
            <label className={styles.field}>
              <span>Trial days</span>
              <input type="number" min={0} value={trialDays} onChange={(e) => setTrialDays(e.target.value)} />
            </label>
          </div>
          <p className={styles.copyHint}>
            Trial days: how long before the monthly plan first bills. The setup fee still charges at checkout.
          </p>

          <label className={styles.checkboxField}>
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            <span>Active</span>
          </label>

          <fieldset className={styles.copySection}>
            <legend>Checkout copy</legend>
            <p className={styles.copyHint}>
              Shown on the Stripe checkout page. Saving updates Stripe immediately.
            </p>
            <label className={styles.field}>
              <span>Plan name</span>
              <input
                value={planName}
                onChange={(e) => setPlanName(e.target.value)}
                placeholder={`${displayName.trim() || "Tier"} Plan`}
              />
            </label>
            <label className={styles.field}>
              <span>Plan description</span>
              <textarea
                value={planDescription}
                onChange={(e) => setPlanDescription(e.target.value)}
                rows={3}
              />
            </label>
            <label className={styles.field}>
              <span>Setup fee name</span>
              <input
                value={setupFeeName}
                onChange={(e) => setSetupFeeName(e.target.value)}
                placeholder={`${displayName.trim() || "Tier"} Setup`}
              />
            </label>
            <label className={styles.field}>
              <span>Setup fee description</span>
              <textarea
                value={setupFeeDescription}
                onChange={(e) => setSetupFeeDescription(e.target.value)}
                rows={3}
              />
            </label>
          </fieldset>

          {error !== null && <p className={styles.error}>{error}</p>}

          <div className={styles.actions}>
            <button type="button" className={styles.secondary} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className={styles.primary} disabled={saving}>
              {saving ? "Saving…" : mode === "create" ? "Create tier" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
